/**
 * @fileoverview User-message edit mode: pick message, save, cancel.
 *
 * @purpose Moves **`handleEditMessage`** / **`handleSaveEdit`** / **`handleCancelEdit`** out of **`KorvoChat.tsx`**
 *          for CRAP / maintainability.
 * @usage **`useKorvoChatMessageEditing(...)`** inside **`KorvoChat`** after message + edit state exists.
 * @ownership KorvoChat component infrastructure
 * @dependencies React **`useCallback`**, **`./types`**
 * @restrictions Only **`role === 'user'`** messages are editable (same as prior inline behavior).
 */

'use client'

import { type Dispatch, type SetStateAction, useCallback } from 'react'

import type { ChatMessage } from './types'

export function useKorvoChatMessageEditing(
  messages: ChatMessage[],
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>,
  editingMessageId: string | null,
  setEditingMessageId: Dispatch<SetStateAction<string | null>>,
  editingContent: string,
  setEditingContent: Dispatch<SetStateAction<string>>,
): {
  handleEditMessage: (messageId: string) => void
  handleSaveEdit: () => void
  handleCancelEdit: () => void
} {
  const handleEditMessage = useCallback(
    (messageId: string) => {
      const message = messages.find((m) => m.id === messageId)
      if (message && message.role === 'user') {
        setEditingMessageId(messageId)
        setEditingContent(message.content)
      }
    },
    [messages, setEditingMessageId, setEditingContent],
  )

  const handleSaveEdit = useCallback(() => {
    if (!editingMessageId || !editingContent.trim()) {
      return
    }
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === editingMessageId ? { ...msg, content: editingContent.trim() } : msg,
      ),
    )
    setEditingMessageId(null)
    setEditingContent('')
  }, [editingMessageId, editingContent, setMessages, setEditingMessageId, setEditingContent])

  const handleCancelEdit = useCallback(() => {
    setEditingMessageId(null)
    setEditingContent('')
  }, [setEditingMessageId, setEditingContent])

  return { handleEditMessage, handleSaveEdit, handleCancelEdit }
}
